// SEO Audit React Hooks
import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  SEOProject,
  SEOPage,
  DashboardMetrics,
  CannibalizationPair,
  PageFilters,
  CrawlProgressResponse,
} from '@/shared/types';
import * as seoApi from '../lib/seoApi';

// Hook for fetching projects list
export function useProjects() {
  const [projects, setProjects] = useState<SEOProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await seoApi.getProjects();
      setProjects(data.projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return { projects, loading, error, refetch: fetchProjects };
}

// Hook for single project
export function useProject(id: string | undefined) {
  const [project, setProject] = useState<SEOProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = useCallback(async () => {
    if (!id) {
      setProject(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await seoApi.getProject(id);
      setProject(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  return { project, loading, error, refetch: fetchProject };
}

// Hook for crawl progress with polling
export function useCrawlProgress(projectId: string | undefined, enabled = true) {
  const [progress, setProgress] = useState<CrawlProgressResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<number | null>(null);

  const fetchProgress = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      const data = await seoApi.getCrawlProgress(projectId);
      setProgress(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch progress');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !enabled) return;

    fetchProgress();

    // Poll every 2 seconds while crawling
    intervalRef.current = window.setInterval(() => {
      if (progress?.job.status === 'running') {
        fetchProgress();
      }
    }, 2000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [projectId, enabled, fetchProgress, progress?.job.status]);

  return { progress, loading, error, refetch: fetchProgress };
}

// Hook for pages with filtering
export function usePages(projectId: string | undefined, initialFilters?: PageFilters) {
  const [pages, setPages] = useState<SEOPage[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PageFilters>(initialFilters || { page: 1, pageSize: 20 });

  const fetchPages = useCallback(async () => {
    if (!projectId) {
      setPages([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await seoApi.getPages(projectId, filters);
      setPages(data.pages);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  }, [projectId, filters]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const updateFilters = useCallback((newFilters: Partial<PageFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  return { pages, total, loading, error, filters, updateFilters, refetch: fetchPages };
}

// Hook for single page
export function usePage(projectId: string | undefined, pageId: string | undefined) {
  const [page, setPage] = useState<SEOPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !pageId) {
      setPage(null);
      setLoading(false);
      return;
    }

    const fetchPage = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await seoApi.getPage(projectId, pageId);
        setPage(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch page');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [projectId, pageId]);

  return { page, loading, error };
}

// Hook for dashboard metrics
export function useMetrics(projectId: string | undefined) {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    if (!projectId) {
      setMetrics(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await seoApi.getMetrics(projectId);
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return { metrics, loading, error, refetch: fetchMetrics };
}

// Hook for cannibalization data
export function useCannibalization(projectId: string | undefined) {
  const [pairs, setPairs] = useState<CannibalizationPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setPairs([]);
      setLoading(false);
      return;
    }

    const fetchCannibalization = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await seoApi.getCannibalization(projectId);
        setPairs(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch cannibalization data');
      } finally {
        setLoading(false);
      }
    };

    fetchCannibalization();
  }, [projectId]);

  return { pairs, loading, error };
}

// Hook for similarity matrix
export function useSimilarityMatrix(projectId: string | undefined, limit = 50) {
  const [data, setData] = useState<{
    pages: { id: string; url: string; title?: string }[];
    matrix: number[][];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setData(null);
      setLoading(false);
      return;
    }

    const fetchMatrix = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await seoApi.getSimilarityMatrix(projectId, limit);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch similarity matrix');
      } finally {
        setLoading(false);
      }
    };

    fetchMatrix();
  }, [projectId, limit]);

  return { data, loading, error };
}

// Hook for keyword clusters
export function useKeywordClusters(projectId: string | undefined) {
  const [clusters, setClusters] = useState<
    { keyword: string; pages: { id: string; url: string; density: number }[] }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setClusters([]);
      setLoading(false);
      return;
    }

    const fetchClusters = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await seoApi.getKeywordClusters(projectId);
        setClusters(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch keyword clusters');
      } finally {
        setLoading(false);
      }
    };

    fetchClusters();
  }, [projectId]);

  return { clusters, loading, error };
}

// Mutation hooks
export function useCreateProject() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createProject = useCallback(async (data: Parameters<typeof seoApi.createProject>[0]) => {
    try {
      setLoading(true);
      setError(null);
      const project = await seoApi.createProject(data);
      return project;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create project';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createProject, loading, error };
}

export function useStartCrawl() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startCrawl = useCallback(async (projectId: string) => {
    try {
      setLoading(true);
      setError(null);
      const job = await seoApi.startCrawl(projectId);
      return job;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start crawl';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { startCrawl, loading, error };
}

export function useDeleteProject() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteProject = useCallback(async (projectId: string) => {
    try {
      setLoading(true);
      setError(null);
      await seoApi.deleteProject(projectId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete project';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteProject, loading, error };
}
